import { useEffect, useState } from 'react';

/**
 * The wait, made legible.
 *
 * An ask takes twenty to forty seconds and used to show one unchanging sentence for all of
 * it, which is indistinguishable from a page that has stopped. These are the pipeline's real
 * phases, in the order the Worker runs them.
 *
 * What they are NOT is progress reported by the server: the ask is one request that answers
 * when it is done, so this advances on elapsed time against measured medians. The wording
 * keeps that honest — it says what is happening, never a percentage or a time remaining —
 * and the last step stays current however long it takes rather than completing on a guess.
 *
 * It takes over the screen rather than sitting under the submit button, where half a minute
 * of work could scroll out of view and leave the page looking idle. The request stays
 * visible behind it: the thing being worked on is the best backdrop for the work, and it
 * comes back when the overlay lifts, so the wait reads as a step in one continuous task
 * rather than a different page.
 *
 * Nothing behind it needs hiding from the keyboard — every control in the form is already
 * `disabled` while an ask is in flight, so there is nothing back there to tab into.
 */
const STEPS = [
  'Reading what you need to teach',
  'Finding your state’s official standards',
  'Matching 240 topics and teaching guidance',
] as const;
/** With a saved set, the second step searches the teacher's own standards too. */
const UPLOAD_STEPS = [STEPS[0], 'Finding your standards', STEPS[2]] as const;

/** Elapsed marks, from asks measured at a median of about 28 seconds end to end. */
const MARKS: readonly number[] = [0, 7000, 17000];
const SLOW_AFTER = 45000;

export function AskProgress({ now = Date.now, uploads = false }: { now?: () => number; uploads?: boolean }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const startedAt = now();
    const timer = window.setInterval(() => setElapsed(now() - startedAt), 500);
    return () => window.clearInterval(timer);
  }, [now]);

  const current = MARKS.reduce((at, mark, index) => (elapsed >= mark ? index : at), 0);

  return <div className="ask-overlay">
    <div className="ask-progress" role="status" aria-label="Finding your standards">
      <p className="ask-progress-title">Finding your standards<span className="title-dot">.</span></p>
      <ol>
        {(uploads ? UPLOAD_STEPS : STEPS).map((step, index) => <li key={step} className={index < current ? 'done' : index === current ? 'current' : 'waiting'}>
          <span className="ask-progress-mark" aria-hidden="true">{index < current ? '✓' : '○'}</span>
          <span>{step}</span>
        </li>)}
      </ol>
      <p className="muted">{elapsed >= SLOW_AFTER
        ? 'Still going. A first search of a subject takes longer than the ones after it.'
        : 'This usually takes about half a minute.'}</p>
    </div>
  </div>;
}
